import {Component, Input, OnInit} from '@angular/core';
import {rnd} from '../../domain/utils/math';
import {Store} from '@ngrx/store';
import {AppState} from '../../state/AppState';

export interface SeriesEntry {
  name: string,
  value: number
}

export interface GraphLine {
  name: string,
  series: SeriesEntry[]
}

interface GraphData {
  legend: boolean;
  showLabels: boolean;
  animations: boolean;
  xAxis: boolean;
  yAxis: boolean;
  showYAxisLabel: boolean;
  showXAxisLabel: boolean;
  xAxisLabel: string;
  yAxisLabel: string;
  xAxisTicks: string[];
  timeline: boolean;
  line: GraphLine[];
}

@Component({
  selector: 'mortgagegraph-tag',
  templateUrl: './graph.component.html',
  styleUrls: ['./graph.component.scss']
})
export class GraphComponent implements OnInit {
  title = 'the-graph';
  principals: GraphData;
  expenses: GraphData;
  interests: GraphData;

  private _showLog: boolean;
  private _lines: GraphLine[];

  colorScheme = {
    domain: ['#5AA454', '#E44D25', '#CFC0BB', '#7aa3e5', '#a8385d', '#aae3f5']
  };

  @Input()
  set lines(value: GraphLine[]) {
    this._lines = value;
    this.calculateLines();
  }

  @Input()
  set showLog(value: boolean) {
    this._showLog = value;

    if (this._lines) {
      this.calculateLines();
    }
  }

  get showLog() {
    return this._showLog;
  }

  calculateLines() {
    console.log('calculate');
    this.principals = {
      legend: false,
      showLabels: true,
      animations: true,
      xAxis: true,
      yAxis: true,
      showYAxisLabel: true,
      showXAxisLabel: false,
      xAxisLabel: 'Year',
      yAxisLabel: 'Principal',
      timeline: true,
      xAxisTicks: this.determineXAxisTicks(this._lines[0]),
      line: [this.scaleYValues(this._lines[0])]
    };

    this.expenses = {
      legend: false,
      showLabels: true,
      animations: true,
      xAxis: true,
      yAxis: true,
      showYAxisLabel: true,
      showXAxisLabel: true,
      xAxisLabel: 'Year',
      yAxisLabel: 'Total Expense',
      timeline: true,
      xAxisTicks: this.determineXAxisTicks(this._lines[2]),
      line: [this.scaleYValues(this._lines[2])]
    };

    this.interests = {
      legend: false,
      showLabels: true,
      animations: true,
      xAxis: true,
      yAxis: true,
      showYAxisLabel: true,
      showXAxisLabel: false,
      xAxisLabel: 'Year',
      yAxisLabel: 'Interest',
      timeline: true,
      xAxisTicks: this.determineXAxisTicks(this._lines[1]),
      line: [this.scaleYValues(this._lines[1])]
    };
  }

  constructor(private store: Store<AppState>) {
  }

  ngOnInit(): void {
  }

  private scaleYValues(source: GraphLine): GraphLine {
    if (this.showLog) {
      return this.logYValues(source);
    } else {
      return this.linearYValues(source);
    }
  }

  private linearYValues(source: GraphLine) {
    return {
      ...source,
      series: source.series.map((entry) => ({
          ...entry,
          origValue: rnd(entry.value, 2)
        })
      )
    };
  }

  private logYValues(source: GraphLine) {
    return {
      ...source,
      series: source.series.map((entry) => ({
          name: entry.name,
          value: (entry.value <= 1) ? 0 : Math.log10(entry.value),
          origValue: rnd(entry.value, 2)
        })
      )
    };
  }


  powScaleYValue(val: number) {
    return Math.round(Math.pow(10, val));
  }

  linearScaleYValue(val: number) {
    return val;
  }

  private determineXAxisTicks(line: GraphLine): string[] {
    const nrOfEntries = line.series.length;
    const firstValue = +line.series[0].name;
    const lastValue = +line.series[nrOfEntries - 1].name;

    const startYear = Math.ceil(firstValue);
    const endYear = Math.floor(lastValue);
    const nrOfTicks = endYear - startYear + 2;

    return Array.from(Array(nrOfTicks - 1).keys()).map((value, index) => String(index + startYear));
  }
}
